import { Link } from "react-router-dom";
import { Atom, ArrowDown, Book, Folder, Calculator, Wrench, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/shared/Header";
import { LearningBreadcrumbs } from "@/components/learning/LearningBreadcrumbs";

const WorldItself = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <LearningBreadcrumbs 
        disciplineName="The World Itself"
        // TODO: Add real learning path data
        showProgress={false}
        compact={true}
      />

      {/* Hero Section */}
      <section className="py-20 px-6 bg-whiter from-primary/5 to-muted/20">
        <div className="container mx-auto text-center max-w-4xl">
          <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-8">
            <Atom className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-5xl font-bold mb-6 bg-white from-primary to-primary/70 bg-clip-text text-transparent">
            The World Itself
          </h1>
          <p className="text-xl text-black leading-relaxed mb-8">
            All disciplines concerned with the structure, operation, and restoration of the physical world and human life. 
            Here we explore the mechanisms of reality and the craft of creation.
          </p>
          <ArrowDown className="mx-auto w-6 h-6 text-black animate-bounce" />
        </div>
      </section>

      {/* Disciplines */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid gap-8">
            
            {/* Mathematics */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white0/10 rounded-full flex items-center justify-center group-hover:bg-white0/20 transition-colors">
                    <Calculator className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Mathematics</CardTitle>
                    <CardDescription className="text-lg">
                      The language of logic, pattern, and structure that underlies all quantitative understanding
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-black">Foundational Fields</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Number Theory & Algebra</li>
                      <li>• Geometry & Topology</li>
                      <li>• Calculus & Analysis</li>
                      <li>• Statistics & Probability</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-black">Applied Mathematics</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Mathematical Logic & Computation</li>
                      <li>• Optimization & Operations Research</li>
                      <li>• Mathematical Modeling</li>
                      <li>• Interdisciplinary Applications</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/study/mathematics">Explore Mathematics</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Natural Sciences */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white0/10 rounded-full flex items-center justify-center group-hover:bg-white0/20 transition-colors">
                    <Atom className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Natural Sciences</CardTitle>
                    <CardDescription className="text-lg">
                      From particle physics to evolutionary biology, exploring the principles and processes that govern the cosmos
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-green-600">Physical Sciences</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Physics & Quantum Mechanics</li>
                      <li>• Chemistry & Molecular Science</li>
                      <li>• Astronomy & Cosmology</li>
                      <li>• Earth Sciences & Geology</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-green-600">Life Sciences</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Evolutionary Biology</li>
                      <li>• Ecology & Environmental Science</li>
                      <li>• Genetics & Molecular Biology</li>
                      <li>• Neuroscience & Cognitive Science</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/study/natural-sciences">Explore Natural Sciences</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Engineering */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white0/10 rounded-full flex items-center justify-center group-hover:bg-white0/20 transition-colors">
                    <Wrench className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Engineering</CardTitle>
                    <CardDescription className="text-lg">
                      Application of scientific principles to design, create, and optimize systems, materials, and infrastructure
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-black">Core Disciplines</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Civil & Structural Engineering</li>
                      <li>• Mechanical Engineering</li>
                      <li>• Electrical Engineering</li>
                      <li>• Chemical Engineering</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-black">Specialized Fields</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Aerospace Engineering</li>
                      <li>• Biomedical Engineering</li>
                      <li>• Environmental Engineering</li>
                      <li>• Materials Engineering</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/study/engineering">Explore Engineering</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Technology */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white0/10 rounded-full flex items-center justify-center group-hover:bg-white0/20 transition-colors">
                    <Zap className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Technology</CardTitle>
                    <CardDescription className="text-lg">
                      Application of scientific knowledge to solve practical problems and develop innovative solutions
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-black">Digital Technologies</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Information Technology & Computer Science</li>
                      <li>• Telecommunications & Networking</li>
                      <li>• Robotics & Automation</li>
                      <li>• Artificial Intelligence</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-black">Applied Technologies</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Environmental & Energy Technologies</li>
                      <li>• Consumer Electronics & Devices</li>
                      <li>• Health Technologies & Bioengineering</li>
                      <li>• Advanced Manufacturing & 3D Printing</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/study/technology">Explore Technology</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Health Sciences */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center group-hover:bg-red-500/20 transition-colors">
                    <Book className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Health Sciences</CardTitle>
                    <CardDescription className="text-lg">
                      The comprehensive study and practice of human health from molecular biology to systems of care
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-red-600">Core Medical Sciences</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Human Anatomy & Physiology</li>
                      <li>• Biochemistry & Molecular Biology</li>
                      <li>• Pathology & Microbiology</li>
                      <li>• Pharmacology & Therapeutics</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-red-600">Clinical & Public Health</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Clinical Medicine & Surgery</li>
                      <li>• Mental Health & Psychology</li>
                      <li>• Public Health & Epidemiology</li>
                      <li>• Allied Health Professions</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/study/health-sciences">Explore Health Sciences</Link>
                </Button>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-16 px-6 bg-muted/30">
        <div className="container mx-auto text-center max-w-4xl">
          <h3 className="text-2xl font-bold mb-6">Continue Your Journey</h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="outline">
              <Link to="/human-inquiry">Explore The Human Inquiry</Link>
            </Button>
            <Button asChild size="lg">
              <Link to="/convergence">Discover Intersections</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorldItself;

